<template>
  <div class="page">
    <div style="display:flex;align-items:center;justify-content:space-between;gap:10px;">
      <h2 class="page-title" style="margin:0;">{{ $t('menu.handlerPool') }}</h2>
      <el-button @click="load" :loading="loading">{{ $t('common.refresh') }}</el-button>
    </div>

    <div class="glass-card" style="margin-top: 14px; padding: 14px;">
      <el-table :data="itemsPage" style="width:100%;" @row-click="open">
        <el-table-column prop="caseNo" :label="$t('field.caseNo')" width="140" />
        <el-table-column prop="title" :label="$t('field.title')" />
        <el-table-column prop="status" :label="$t('field.status')" width="150">
          <template slot-scope="s">
            <span class="status-pill-wrap">
              <span :class="['tag-pill', s.row.hasReminder ? 'tag-pill--with-dot' : '']">{{ caseStatusLabel(s.row) }}</span>
              <span v-if="s.row.hasReminder" class="status-pill-dot" />
            </span>
          </template>
        </el-table-column>
        <el-table-column :label="flowColumnLabel" width="220">
          <template slot-scope="s">
            <span class="tag-pill">{{ flowLabel(s.row) }}</span>
          </template>
        </el-table-column>
        <el-table-column :label="$t('field.timeout')" width="140">
          <template slot-scope="s">
            <span :class="['tag-pill', isTimeout30(s.row) ? 'tag-pill--danger' : '']">{{ isTimeout30(s.row) ? $t('handler.timeout30') : '-' }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="createdAtLabel" :label="$t('field.createdAt')" width="190" />
        <el-table-column :label="$t('common.actions')" width="140">
          <template slot-scope="s">
            <el-button type="primary" size="mini" class="btn-glass" @click.stop="take(s.row)">{{ $t('handler.take') }}</el-button>
          </template>
        </el-table-column>
      </el-table>

      <div style="display:flex;justify-content:flex-end;margin-top:12px;">
        <el-pagination
          background
          layout="total, sizes, prev, pager, next"
          :total="items.length"
          :page-size="pageSize"
          :current-page="page"
          :page-sizes="[5,10,20,50]"
          @current-change="(p) => (page = p)"
          @size-change="onSizeChange"
        />
      </div>
    </div>

    <HandlerCaseDialog v-model="detailDialog" :case-no="currentCaseNo" @reminder-read="onReminderRead" />
  </div>
</template>

<script>
import { handlerPool, takeCase } from '../../api/tickets';
import HandlerCaseDialog from '../../components/HandlerCaseDialog.vue';
import { caseStatusLabel as formatCaseStatusLabel, flowLabel as formatFlowLabel, localText as formatLocalText } from '../../utils/caseDisplay';

export default {
  name: 'HandlerPool',
  components: { HandlerCaseDialog },
  data() {
    return { loading: false, items: [], page: 1, pageSize: 10, detailDialog: false, currentCaseNo: '', nowTs: Date.now(), tickTimer: null };
  },
  created() {
    this.load();
    this.maybeOpenFromQuery();
    this.tickTimer = setInterval(() => {
      this.nowTs = Date.now();
    }, 30 * 1000);
  },
  beforeDestroy() {
    if (this.tickTimer) clearInterval(this.tickTimer);
    this.tickTimer = null;
  },
  computed: {
    version() {
      return this.$store.state.versions.handlerCases;
    },
    itemsPage() {
      const start = (this.page - 1) * this.pageSize;
      return (this.items || []).slice(start, start + this.pageSize);
    },
    flowColumnLabel() {
      return formatLocalText(this, '流转', 'Flow');
    }
  },
  watch: {
    version() {
      this.load();
    },
    detailDialog(v) {
      if (v) return;
      if (this.$route.query && this.$route.query.caseNo) {
        const q = { ...this.$route.query };
        delete q.caseNo;
        this.$router.replace({ path: this.$route.path, query: q });
      }
    },
    '$route.query.caseNo'() {
      this.maybeOpenFromQuery();
    }
  },
  methods: {
    maybeOpenFromQuery() {
      const caseNo = this.$route.query && this.$route.query.caseNo ? String(this.$route.query.caseNo) : '';
      if (!caseNo) return;
      this.currentCaseNo = caseNo;
      this.detailDialog = true;
    },
    onSizeChange(s) {
      this.pageSize = s;
      this.page = 1;
    },
    isTimeout30(row) {
      const base = row.resubmittedAt || row.createdAt;
      if (!base) return false;
      return this.nowTs - new Date(base).getTime() > 30 * 60 * 1000;
    },
    caseStatusLabel(row) {
      return formatCaseStatusLabel(this, row);
    },
    flowLabel(row) {
      return formatFlowLabel(this, row);
    },
    async load() {
      if (!this.$store.state.user) return;
      this.loading = true;
      try {
        const r = await handlerPool();
        this.items = (r || []).map((x) => ({
          ...x,
          createdAtLabel: x.createdAt ? new Date(x.createdAt).toLocaleString() : '-'
        }));
        const maxPage = Math.max(1, Math.ceil(this.items.length / this.pageSize));
        this.page = Math.min(this.page, maxPage);
      } finally {
        this.loading = false;
      }
    },
    open(row) {
      if (!row || !row.caseNo) return;
      this.currentCaseNo = row.caseNo;
      this.detailDialog = true;
    },
    onReminderRead(caseNo) {
      if (!caseNo) return;
      this.items = (this.items || []).map((x) => String(x.caseNo) === String(caseNo) ? { ...x, hasReminder: false } : x);
      this.$store.dispatch('refreshHandlerBadges');
    },
    async take(row) {
      if (!row || !row.caseNo) return;
      try {
        await takeCase(row.caseNo);
        this.$message.success(this.$t('handler.take'));
        // Make the state transition visible immediately even if SSE is delayed.
        this.$store.commit('bumpVersion', 'handlerCases');
        this.$store.dispatch('refreshHandlerBadges');
        this.$router.push('/handler/in-progress');
      } catch (e) {}
    }
  }
};
</script>

<style scoped>
.status-pill-wrap {
  position: relative;
  display: inline-block;
}
.tag-pill--with-dot {
  padding-right: 18px;
}
.status-pill-dot {
  position: absolute;
  top: 3px;
  right: 5px;
  width: 7px;
  height: 7px;
  border-radius: 99px;
  background: rgba(239, 68, 68, 0.95);
  border: 1px solid rgba(255, 255, 255, 0.95);
}
</style>
